import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { API } from 'aws-amplify';
import { listEventLists, listVideoData, listSessionLists } from '../../graphql/queries';

/**
 * WatchSelection - two-step picker for the broadcast viewer.
 *
 * Step 1: Pick an event
 * Step 2: Pick a run (shows runId + camera count)
 *
 * Props:
 *   onLaunch(videoDataArray, label) - called when user clicks Watch
 */
function WatchSelection({ onLaunch }) {
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState(null);

  const [selectedEventId, setSelectedEventId] = useState(''); // stores ev.id (DynamoDB UUID)
  const [runs, setRuns] = useState([]); // [{ runId, videos: [] }]
  const [runsLoading, setRunsLoading] = useState(false);

  const [selectedSessionKey, setSelectedSessionKey] = useState(''); // sessionId or runId
  const [launching, setLaunching] = useState(false);

  // Load events on mount
  useEffect(() => {
    const fetch = async () => {
      try {
        const result = await API.graphql({
          query: listEventLists,
          variables: { limit: 100 },
        });
        const items = (result.data.listEventLists.items || [])
          .filter((e) => !e._deleted)
          .sort((a, b) => {
            // Most recent first
            const da = a.eventStartDate || a.createdAt || '';
            const db = b.eventStartDate || b.createdAt || '';
            return db.localeCompare(da);
          });
        setEvents(items);
      } catch (err) {
        console.error('WatchSelection: failed to load events', err);
        setEventsError('Failed to load events. Please refresh.');
      } finally {
        setEventsLoading(false);
      }
    };
    fetch();
  }, []);

  // Load sessions + videos for selected event
  useEffect(() => {
    if (!selectedEventId) {
      setRuns([]);
      setSelectedSessionKey('');
      return;
    }

    const fetch = async () => {
      setRunsLoading(true);
      setRuns([]);
      setSelectedSessionKey('');
      try {
        const [sessionsResult, videosResult] = await Promise.all([
          API.graphql({
            query: listSessionLists,
            variables: { filter: { eventId: { eq: selectedEventId } }, limit: 100 },
          }),
          API.graphql({
            query: listVideoData,
            variables: { filter: { eventId: { eq: selectedEventId } }, limit: 200 },
          }),
        ]);

        const sessions = (sessionsResult.data.listSessionLists.items || []).filter(
          (s) => !s._deleted
        );
        const sessionMap = Object.fromEntries(sessions.map((s) => [s.id, s]));

        const videos = (videosResult.data.listVideoData.items || []).filter(
          (v) => !v._deleted
        );

        // Group by sessionId (new) falling back to runId (old)
        const byKey = {};
        videos.forEach((v) => {
          const key = v.sessionId || v.runId;
          if (!key) return;
          if (!byKey[key]) byKey[key] = [];
          byKey[key].push(v);
        });

        const runList = Object.entries(byKey).map(([key, vids]) => {
          const session = sessionMap[key];
          const label = session?.sessionName
            || (vids[0]?.sessionId ? `Session ${key.slice(-6)}` : `Run ${key}`);
          return {
            key,
            label,
            sessionStart: session?.sessionStart || vids[0]?.createdAt || '',
            videos: vids,
            cameraCount: vids.length,
            hasStreams: vids.some(
              (v) => v.streamUrl || v.youtubeStreamUrl || v.tracksideVideoUrl
            ),
          };
        });

        // Most recent first
        runList.sort((a, b) => b.sessionStart.localeCompare(a.sessionStart));
        setRuns(runList);
      } catch (err) {
        console.error('WatchSelection: failed to load sessions', err);
      } finally {
        setRunsLoading(false);
      }
    };
    fetch();
  }, [selectedEventId]);

  const handleLaunch = () => {
    const run = runs.find((r) => r.key === selectedSessionKey);
    if (!run) return;

    const event = events.find((e) => e.id === selectedEventId);
    const label = [event?.eventName, event?.eventVenue, run.label]
      .filter(Boolean)
      .join(' · ');

    onLaunch(run.videos, label);
  };

  const selectedEvent = events.find((e) => e.id === selectedEventId);

  return (
    <Box
      sx={{
        maxWidth: 540,
        mx: 'auto',
        mt: 6,
        px: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      <Typography variant="h5" fontWeight="bold">
        Watch Now
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Select an event and run to watch synchronized camera feeds.
      </Typography>

      {eventsError && <Alert severity="error">{eventsError}</Alert>}

      {/* Event picker */}
      <FormControl fullWidth disabled={eventsLoading}>
        <InputLabel id="event-select-label">Event</InputLabel>
        <Select
          labelId="event-select-label"
          value={selectedEventId || ''}
          label="Event"
          onChange={(e) => setSelectedEventId(e.target.value || '')}
        >
          {eventsLoading ? (
            <MenuItem disabled>
              <CircularProgress size={16} sx={{ mr: 1 }} /> Loading events…
            </MenuItem>
          ) : (
            events.map((ev) => (
              <MenuItem key={ev.id} value={ev.id}>
                <Box>
                  <Typography variant="body2">{ev.eventName || `Event ${ev.eventId}`}</Typography>
                  {(ev.eventVenue || ev.eventStartDate) && (
                    <Typography variant="caption" color="text.secondary">
                      {[ev.eventVenue, ev.eventStartDate].filter(Boolean).join(' · ')}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>

      {/* Session picker (shown after event selected) */}
      {selectedEventId && (
        <FormControl fullWidth disabled={runsLoading || runs.length === 0}>
          <InputLabel id="run-select-label">Session</InputLabel>
          <Select
            labelId="run-select-label"
            value={selectedSessionKey || ''}
            label="Session"
            onChange={(e) => setSelectedSessionKey(e.target.value || '')}
          >
            {runsLoading ? (
              <MenuItem disabled>
                <CircularProgress size={16} sx={{ mr: 1 }} /> Loading sessions…
              </MenuItem>
            ) : runs.length === 0 ? (
              <MenuItem disabled>No sessions with video found</MenuItem>
            ) : (
              runs.map((run) => (
                <MenuItem key={run.key} value={run.key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">{run.label}</Typography>
                    <Chip
                      label={`${run.cameraCount} cam${run.cameraCount !== 1 ? 's' : ''}`}
                      size="small"
                      color={run.hasStreams ? 'success' : 'default'}
                      variant="outlined"
                    />
                    {!run.hasStreams && (
                      <Chip label="no streams" size="small" color="warning" variant="outlined" />
                    )}
                  </Box>
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      )}

      {/* Launch button */}
      {selectedSessionKey && (
        <Button
          variant="contained"
          color="success"
          size="large"
          startIcon={<PlayArrowIcon />}
          onClick={handleLaunch}
          disabled={launching}
          sx={{ alignSelf: 'flex-start' }}
        >
          Watch
        </Button>
      )}
    </Box>
  );
}

export default WatchSelection;
